import React, { useState, useEffect, useRef, useCallback, useMemo, memo } from 'react';
import Particles, { initParticlesEngine } from '@tsparticles/react';
import { loadSlim } from '@tsparticles/slim';
import { loadSnowPreset } from '@tsparticles/preset-snow';
import { useHistory } from '../../contexts/HistoryContext';
import { usePlayer } from '../../contexts/PlayerContext';
import { API_ORIGIN } from '../../config/apiOrigin';
import { useFlexibleAlbumPalette } from '../../hooks/useFlexibleAlbumPalette';
import { getTrackPattern } from '../../utils/equalizerPatterns';
import { getYearEmoji } from '../../utils/yearIcons';
import { getThemeParticlesOptions } from './themeParticles';
import { createRGBA } from './rgbaEngine';
import Button from '../ui/Button';
import ConfirmModal from '../ConfirmModal';

import './VintagePlayer.css';

// Keep legacy particle layer for non-snow-white themes.
const ThemeParticlesLayer = memo(({ paletteId, isParticlesInit, prefersReducedMotion }) => {
    const particleOptions = useMemo(() => getThemeParticlesOptions(paletteId), [paletteId]);

    if (!isParticlesInit || prefersReducedMotion || paletteId === 'snow-white' || paletteId === 'cupid-pink') {
        return null;
    }

    return (
        <div className="theme-particles-layer" aria-hidden="true">
            <Particles
                className="theme-particles-canvas"
                id="theme-particles"
                style={{ position: 'absolute', inset: 0 }}
                options={particleOptions}
            />
        </div>
    );
}, (prevProps, nextProps) => {
    return (
        prevProps.paletteId === nextProps.paletteId &&
        prevProps.isParticlesInit === nextProps.isParticlesInit &&
        prevProps.prefersReducedMotion === nextProps.prefersReducedMotion
    );
});

const RADIO_BG_SHADER_MOON = `
float hash(vec2 p) {
    return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453123);
}

float noise2(vec2 p) {
    vec2 i = floor(p);
    vec2 f = fract(p);
    vec2 u = f * f * (3.0 - 2.0 * f);
    float a = hash(i + vec2(0.0, 0.0));
    float b = hash(i + vec2(1.0, 0.0));
    float c = hash(i + vec2(0.0, 1.0));
    float d = hash(i + vec2(1.0, 1.0));
    return mix(mix(a, b, u.x), mix(c, d, u.x), u.y);
}

float fbm(vec2 p) {
    float v = 0.0;
    float amp = 0.5;
    for (int i = 0; i < 5; i++) {
        v += amp * noise2(p);
        p = p * 2.02 + vec2(21.1, 9.7);
        amp *= 0.5;
    }
    return v;
}

float sdMoon(vec2 p, float d, float ra, float rb) {
    p.y = abs(p.y);
    float a = (ra * ra - rb * rb + d * d) / (2.0 * d);
    float b = sqrt(max(ra * ra - a * a, 0.0));
    if (d * (p.x * b - p.y * a) > d * d * max(b - p.y, 0.0)) {
        return length(p - vec2(a, b));
    }
    return max(length(p) - ra, -(length(p - vec2(d, 0.0)) - rb));
}

void main() {
    vec2 uv = gl_FragCoord.xy / resolution.xy;
    vec2 p = uv * 2.0 - 1.0;
    p.x *= resolution.x / max(resolution.y, 1.0);

    // Full-scene night gradient that always spans the entire background.
    float skyMix = clamp(uv.y * 1.08 + 0.07, 0.0, 1.0);
    vec3 sky = mix(tintA * 0.78, tintB * 1.08, skyMix);

    // Large moon/crescent orbiting around the scene.
    vec2 orbitCenter = vec2(0.28, 0.72);
    vec2 orbitRadius = vec2(0.44, 0.04);
    vec2 moonPos = orbitCenter + vec2(cos(time * 0.24), sin(time * 0.18)) * orbitRadius;
    vec2 mp = p - moonPos;
    vec2 mpPixel = floor(mp * 96.0) / 96.0;
    float selfSpin = floor(time * 12.0) / 12.0;
    float moonMask = 1.0 - smoothstep(0.006, 0.018, sdMoon(mpPixel, 0.072 + sin(time * 0.18) * 0.015, 0.225, 0.20));

    // Y-axis spin (pseudo-3D): rotate surface coordinates on sphere, not silhouette.
    vec2 moonN = mpPixel / 0.24;
    float r2 = dot(moonN, moonN);
    float z = sqrt(max(1.0 - r2, 0.0));
    float sx = moonN.x * cos(selfSpin) + z * sin(selfSpin);
    float sz = z * cos(selfSpin) - moonN.x * sin(selfSpin);
    vec2 moonSpinUv = vec2(sx, moonN.y);

    // Lunar texture and crater-like tonal variation.
    vec2 moonUv = floor((moonSpinUv * 2.2) * 56.0) / 56.0;
    float lunar = fbm(moonUv * 1.9 + vec2(time * 0.02, -time * 0.015));
    float crater = fbm(moonUv * 5.4 - vec2(time * 0.03, 0.0));
    float craterMask = smoothstep(0.42, 0.9, crater) * 0.3;
    vec3 moonBase = vec3(0.78, 0.85, 0.98);
    vec3 moonShade = vec3(0.34, 0.44, 0.66);
    vec3 moonColor = mix(moonShade, moonBase, clamp(lunar + 0.23, 0.0, 1.0));
    float light = clamp(0.35 + 0.65 * (0.36 * sx + 0.18 * moonN.y + 0.92 * sz), 0.0, 1.0);
    moonColor *= mix(0.7, 1.02, light);
    moonColor -= craterMask;

    // Soft halo around moon for atmospheric glow.
    float distToMoon = length(mp);
    float halo = smoothstep(0.9, 0.05, distToMoon) * 0.2;
    vec3 color = sky + vec3(0.30, 0.43, 0.74) * halo;

    // Sparse stars with tiny twinkle.
    float starField = step(0.9965, hash(floor(uv * resolution.xy * 0.35)));
    starField *= 0.45 + 0.55 * sin(time * 2.3 + uv.x * 130.0 + uv.y * 71.0);
    color += vec3(starField) * 0.32;

    // Composite moon on top.
    color = mix(color, moonColor, moonMask);

    // Gentle scanline shimmer for retro screen vibe.
    float scan = sin(uv.y * resolution.y * 0.18 - time * 5.0) * 0.02;
    color += vec3(scan);

    gl_FragColor = vec4(color, 0.5);
}
`;

const RADIO_BG_SHADER_SUN = `
float hash(vec2 p) {
    return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453123);
}

float noise2(vec2 p) {
    vec2 i = floor(p);
    vec2 f = fract(p);
    vec2 u = f * f * (3.0 - 2.0 * f);
    float a = hash(i + vec2(0.0, 0.0));
    float b = hash(i + vec2(1.0, 0.0));
    float c = hash(i + vec2(0.0, 1.0));
    float d = hash(i + vec2(1.0, 1.0));
    return mix(mix(a, b, u.x), mix(c, d, u.x), u.y);
}

void main() {
    vec2 uv = gl_FragCoord.xy / resolution.xy;
    vec2 p = uv * 2.0 - 1.0;
    p.x *= resolution.x / max(resolution.y, 1.0);

    vec3 sky = mix(tintA * 0.96, tintB * 1.03, clamp(uv.y * 1.04, 0.0, 1.0));

    vec2 orbitCenter = vec2(0.18, 0.74);
    vec2 orbitRadius = vec2(0.62, 0.05);
    vec2 sunPos = orbitCenter + vec2(cos(time * 0.26), sin(time * 0.19)) * orbitRadius;
    vec2 sp = p - sunPos;
    vec2 spPixel = floor(sp * 60.0) / 60.0;
    float radius = 0.135;
    float ang = atan(spPixel.y, spPixel.x);
    float sunMask = 1.0 - smoothstep(radius, radius + 0.012, length(spPixel));
    float dist = length(spPixel);
    float rayStep = 6.2831853 / 8.0;
    float nearestRay = floor((ang / rayStep) + 0.5) * rayStep;
    vec2 rayDir = vec2(cos(nearestRay), sin(nearestRay));
    vec2 rayOrtho = vec2(-rayDir.y, rayDir.x);
    float rayAlong = dot(spPixel, rayDir);
    float rayAcross = abs(dot(spPixel, rayOrtho));
    float rayBody = step(radius + 0.025, rayAlong) * (1.0 - step(radius + 0.1025, rayAlong));
    float rayWidth = 1.0 - step(0.013, rayAcross);
    float rayMask = rayBody * rayWidth;

    float selfSpin = floor(time * 12.0) / 12.0;
    vec2 sunN = spPixel / radius;
    float r2 = dot(sunN, sunN);
    float z = sqrt(max(1.0 - r2, 0.0));
    float sx = sunN.x * cos(selfSpin) + z * sin(selfSpin);
    float sz = z * cos(selfSpin) - sunN.x * sin(selfSpin);
    vec2 spinUv = vec2(sx, sunN.y);
    vec2 spinPixelUv = floor((spinUv * 2.2) * 24.0) / 24.0;

    float bands = 0.5 + 0.5 * sin(spinPixelUv.y * 18.0 + selfSpin * 2.2);
    float gran = noise2(spinPixelUv * 7.0 + vec2(time * 0.05, 0.0));
    vec3 sunCore = vec3(1.00, 0.88, 0.44);
    vec3 sunDeep = vec3(1.00, 0.66, 0.26);
    vec3 sunColor = mix(sunDeep, sunCore, clamp(0.55 * bands + 0.45 * gran, 0.0, 1.0));
    vec3 rayColor = vec3(1.0, 0.78, 0.38);
    float light = clamp(0.4 + 0.6 * (0.44 * sx + 0.9 * sz), 0.0, 1.0);
    sunColor *= mix(0.76, 1.08, light);

    // Posterize to emphasize 8-bit palette steps.
    sunColor = floor(sunColor * 7.0) / 7.0;
    rayColor = floor(rayColor * 7.0) / 7.0;

    float halo = smoothstep(0.72, 0.0, length(sp));
    vec3 color = sky + vec3(1.0, 0.75, 0.35) * halo * 0.25;
    color = mix(color, sunColor, sunMask);
    color = mix(color, rayColor, rayMask);

    float scan = sin(uv.y * resolution.y * 0.2 - time * 5.0) * 0.015;
    color += vec3(scan);
    gl_FragColor = vec4(color, 0.48);
}
`;

const RADIO_TOP_SHADER_MOON = `
float hash(vec2 p) {
    return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453123);
}

float sdMoon(vec2 p, float d, float ra, float rb) {
    p.y = abs(p.y);
    float a = (ra * ra - rb * rb + d * d) / (2.0 * d);
    float b = sqrt(max(ra * ra - a * a, 0.0));
    if (d * (p.x * b - p.y * a) > d * d * max(b - p.y, 0.0)) {
        return length(p - vec2(a, b));
    }
    return max(length(p) - ra, -(length(p - vec2(d, 0.0)) - rb));
}

void main() {
    vec2 uv = gl_FragCoord.xy / resolution.xy;
    vec2 p = uv * 2.0 - 1.0;
    p.x *= resolution.x / max(resolution.y, 1.0);

    vec2 orbitCenter = vec2(0.28, 0.72);
    vec2 orbitRadius = vec2(0.44, 0.04);
    vec2 moonPos = orbitCenter + vec2(cos(time * 0.24), sin(time * 0.18)) * orbitRadius;
    vec2 mp = p - moonPos;
    vec2 mpPixel = floor(mp * 88.0) / 88.0;
    float selfSpin = floor(time * 12.0) / 12.0;
    float moonShape = 1.0 - smoothstep(0.004, 0.014, sdMoon(mpPixel, 0.078 + sin(time * 0.14) * 0.013, 0.24, 0.215));

    vec2 moonN = mpPixel / 0.24;
    float r2 = dot(moonN, moonN);
    float z = sqrt(max(1.0 - r2, 0.0));
    float sx = moonN.x * cos(selfSpin) + z * sin(selfSpin);
    float sz = z * cos(selfSpin) - moonN.x * sin(selfSpin);
    vec2 moonSpinUv = vec2(sx, moonN.y);

    // Moon disc overlay so it remains visible on top of containers.
    float moonNoise = (hash(floor((moonSpinUv + 2.0) * 84.0)) - 0.5) * 0.08;
    vec3 moonColor = vec3(0.79, 0.88, 1.0) + moonNoise;
    float light = clamp(0.34 + 0.66 * (0.34 * sx + 0.2 * moonN.y + 0.92 * sz), 0.0, 1.0);
    moonColor *= mix(0.72, 1.02, light);

    float dist = length(p - moonPos);
    float halo = smoothstep(0.95, 0.06, dist);
    float rim = smoothstep(0.33, 0.16, dist) * 0.82;

    float sweep = sin((uv.y * 240.0) - (time * 5.4)) * 0.04;
    float grain = (hash(gl_FragCoord.xy + vec2(time * 41.0, time * 13.0)) - 0.5) * 0.05;

    float stars = step(0.997, hash(floor(uv * resolution.xy * 0.42)));
    stars *= 0.5 + 0.5 * sin(time * 2.1 + uv.x * 140.0 + uv.y * 75.0);

    vec3 tint = mix(tintA, tintB, 0.7);
    vec3 crtPass = tint * halo * 0.48 + vec3(stars) * 0.26 + vec3(sweep + grain);
    vec3 moonPass = moonColor * moonShape + vec3(0.72, 0.84, 1.0) * rim * 0.55;

    float alphaMoon = clamp(moonShape * 0.58 + rim * 0.16, 0.0, 0.68);
    float alphaCrt = clamp(0.12 + halo * 0.12, 0.0, 0.24);
    float outAlpha = clamp(alphaMoon + alphaCrt, 0.0, 0.9);

    vec3 outColor = moonPass * alphaMoon + crtPass * alphaCrt;
    if (outAlpha > 0.0001) {
        outColor /= outAlpha;
    }

    gl_FragColor = vec4(outColor, outAlpha);
}
`;

const RADIO_TOP_SHADER_SUN = `
float hash(vec2 p) {
    return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453123);
}

void main() {
    vec2 uv = gl_FragCoord.xy / resolution.xy;
    vec2 p = uv * 2.0 - 1.0;
    p.x *= resolution.x / max(resolution.y, 1.0);

    vec2 orbitCenter = vec2(0.18, 0.74);
    vec2 orbitRadius = vec2(0.62, 0.05);
    vec2 sunPos = orbitCenter + vec2(cos(time * 0.26), sin(time * 0.19)) * orbitRadius;
    vec2 sp = p - sunPos;
    vec2 spPixel = floor(sp * 56.0) / 56.0;
    float radius = 0.135;
    float ang = atan(spPixel.y, spPixel.x);
    float sunShape = 1.0 - smoothstep(radius, radius + 0.014, length(spPixel));
    float rayStep = 6.2831853 / 8.0;
    float nearestRay = floor((ang / rayStep) + 0.5) * rayStep;
    vec2 rayDir = vec2(cos(nearestRay), sin(nearestRay));
    vec2 rayOrtho = vec2(-rayDir.y, rayDir.x);
    float rayAlong = dot(spPixel, rayDir);
    float rayAcross = abs(dot(spPixel, rayOrtho));
    float rayBody = step(radius + 0.025, rayAlong) * (1.0 - step(radius + 0.1075, rayAlong));
    float rayWidth = 1.0 - step(0.0135, rayAcross);
    float rayShape = rayBody * rayWidth;

    float selfSpin = floor(time * 12.0) / 12.0;
    vec2 sunN = spPixel / radius;
    float r2 = dot(sunN, sunN);
    float z = sqrt(max(1.0 - r2, 0.0));
    float sx = sunN.x * cos(selfSpin) + z * sin(selfSpin);
    float sz = z * cos(selfSpin) - sunN.x * sin(selfSpin);
    vec2 spinUv = vec2(sx, sunN.y);
    vec2 spinUvPx = floor(spinUv * 30.0) / 30.0;
    float bands = 0.5 + 0.5 * sin(spinUvPx.y * 16.0 + selfSpin * 2.2);
    float noise = (hash(floor((spinUvPx + 2.0) * 58.0)) - 0.5) * 0.1;

    vec3 sunColor = mix(vec3(1.0, 0.66, 0.26), vec3(1.0, 0.88, 0.44), bands) + noise;
    vec3 rayColor = vec3(1.0, 0.8, 0.42);
    float light = clamp(0.42 + 0.58 * (0.44 * sx + 0.9 * sz), 0.0, 1.0);
    sunColor *= mix(0.8, 1.08, light);
    sunColor = floor(sunColor * 7.0) / 7.0;
    rayColor = floor(rayColor * 7.0) / 7.0;

    float dist = length(sp);
    float halo = smoothstep(0.88, 0.06, dist);
    float rim = smoothstep(0.45, 0.26, dist) * 0.72;
    float sweep = sin((uv.y * 220.0) - (time * 5.2)) * 0.035;
    float grain = (hash(gl_FragCoord.xy + vec2(time * 39.0, time * 14.0)) - 0.5) * 0.045;

    vec3 tint = mix(tintA, tintB, 0.66);
    vec3 crtPass = tint * halo * 0.36 + vec3(sweep + grain);
    vec3 sunPass = sunColor * sunShape + rayColor * rayShape + vec3(1.0, 0.86, 0.52) * rim * 0.5;

    float alphaSun = clamp(sunShape * 0.5 + rim * 0.12, 0.0, 0.58);
    alphaSun = clamp(alphaSun + rayShape * 0.18, 0.0, 0.68);
    float alphaCrt = clamp(0.08 + halo * 0.1, 0.0, 0.18);
    float outAlpha = clamp(alphaSun + alphaCrt, 0.0, 0.72);
    vec3 outColor = sunPass * alphaSun + crtPass * alphaCrt;
    if (outAlpha > 0.0001) outColor /= outAlpha;
    gl_FragColor = vec4(outColor, outAlpha);
}
`;

const RADIO_TOP_SHADER_HEARTS = `
float hash(vec2 p) {
    return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453123);
}

float maskBox(vec2 p, vec2 halfSize) {
    vec2 d = abs(p) - halfSize;
    return 1.0 - step(0.0, max(d.x, d.y));
}

void main() {
    vec2 uv = gl_FragCoord.xy / resolution.xy;
    vec3 tint = mix(tintA, tintB, 0.7);
    vec3 heartColorA = vec3(1.0, 0.53, 0.78);
    vec3 heartColorB = vec3(1.0, 0.71, 0.88);

    float t = floor(time * 12.0) / 12.0;
    float hearts = 0.0;

    // Pixel-heart stream drifting downward with slight horizontal sway.
    for (int i = 0; i < 18; i++) {
        float fi = float(i);
        float laneX = fract(hash(vec2(fi * 6.9, 2.1)) + fi * 0.19);
        float speed = 0.10 + 0.05 * fract(hash(vec2(fi * 2.7, 8.5)));
        float y = 1.12 - fract(t * speed + fi * 0.081) * 1.35; // top -> bottom
        float sway = sin(t * (1.1 + fi * 0.07) + fi * 1.8) * 0.03;
        vec2 p = vec2((uv.x - laneX) - sway, uv.y - y);

        float size = 0.016 + 0.008 * fract(hash(vec2(fi * 9.1, 4.2)));
        p /= size;
        p = floor(p * 10.0) / 10.0;

        // True heart silhouette (implicit equation), then pixel-quantized.
        // Keep point facing upward in screen coordinates.
        vec2 hp = vec2(p.x, p.y) * 1.35;
        float x2 = hp.x * hp.x;
        float y2 = hp.y * hp.y;
        float h = (x2 + y2 - 1.0);
        float heartEq = h * h * h - x2 * hp.y * y2;
        float heart = 1.0 - step(0.0, heartEq);

        // Chunky 8-bit edge accents.
        float rim = 1.0 - step(0.09, abs(heartEq));
        heart = max(heart, rim * 0.25);
        hearts = max(hearts, heart);
    }

    float scanA = sin((uv.y * resolution.y * 0.21) - (time * 4.9)) * 0.03;
    float grain = (hash(gl_FragCoord.xy + vec2(time * 41.0, time * 17.0)) - 0.5) * 0.045;
    vec3 crtPass = tint * (scanA + grain);
    vec3 heartPass = mix(heartColorA, heartColorB, fract(uv.x * 7.0 + uv.y * 5.0)) * hearts;

    float alphaHearts = hearts * 0.55;
    float alphaCrt = 0.12;
    float outAlpha = clamp(alphaHearts + alphaCrt, 0.0, 0.72);
    vec3 outColor = heartPass * alphaHearts + crtPass * alphaCrt;
    if (outAlpha > 0.0001) outColor /= outAlpha;
    outColor = floor(outColor * 8.0) / 8.0;
    gl_FragColor = vec4(outColor, outAlpha);
}
`;

const RADIO_TOP_SHADER_OBJECTS = `
float hash(vec2 p) {
    return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453123);
}

mat2 rot2(float a) {
    float c = cos(a);
    float s = sin(a);
    return mat2(c, -s, s, c);
}

float maskCircle(vec2 p, float r) {
    return 1.0 - step(r, length(p));
}

float maskBox(vec2 p, vec2 halfSize) {
    vec2 d = abs(p) - halfSize;
    return 1.0 - step(0.0, max(d.x, d.y));
}

void main() {
    vec2 uv = gl_FragCoord.xy / resolution.xy;
    vec2 p = uv * 2.0 - 1.0;
    p.x *= resolution.x / max(resolution.y, 1.0);

    float t = floor(time * 12.0) / 12.0;
    vec2 objPos = vec2(0.28, 0.58) + vec2(cos(t * 0.22), sin(t * 0.14)) * vec2(0.34, 0.05);
    vec2 lp = floor((p - objPos) * 86.0) / 86.0;

    vec3 accent = mix(tintA, tintB, 0.72);
    vec3 bright = clamp(mix(accent, vec3(1.0), 0.42), 0.0, 1.0);
    vec3 deep = clamp(mix(accent, vec3(0.0), 0.35), 0.0, 1.0);
    vec3 objColor = vec3(0.0);
    float objMask = 0.0;

    // Mode 3: Sage windmill + leaves
    if (mode > 2.5 && mode < 3.5) {
        float mast = maskBox(lp - vec2(0.0, -0.09), vec2(0.015, 0.12));
        float hub = maskCircle(lp + vec2(0.0, 0.02), 0.03);
        vec2 bp = rot2(t * 0.8) * (lp + vec2(0.0, 0.02));
        float bladeA = maskBox(bp - vec2(0.06, 0.0), vec2(0.055, 0.012));
        float bladeB = maskBox(bp + vec2(0.06, 0.0), vec2(0.055, 0.012));
        float bladeC = maskBox(bp - vec2(0.0, 0.06), vec2(0.012, 0.055));
        float bladeD = maskBox(bp + vec2(0.0, 0.06), vec2(0.012, 0.055));
        float leaves = 0.0;
        for (int i = 0; i < 4; i++) {
            float fi = float(i);
            vec2 lv = lp - vec2(-0.18 + fi * 0.12 + sin(t * 0.6 + fi) * 0.02, 0.16 - fi * 0.05);
            leaves = max(leaves, maskBox(rot2(0.55 + fi * 0.3) * lv, vec2(0.015, 0.008)));
        }
        objMask = max(mast, max(hub, max(max(bladeA, bladeB), max(bladeC, bladeD))));
        objColor = mix(deep, bright, objMask);
        objColor = mix(objColor, bright, leaves);
        objMask = max(objMask, leaves);
    }
    // Mode 4: Charcoal cassette reel
    else if (mode > 3.5 && mode < 4.5) {
        float outer = maskCircle(lp, 0.14);
        float inner = maskCircle(lp, 0.05);
        float ring = max(0.0, outer - inner);
        float spokes = 0.0;
        vec2 sp = rot2(t * 1.1) * lp;
        for (int i = 0; i < 6; i++) {
            float a = float(i) * 1.0472;
            vec2 rp = rot2(a) * sp;
            spokes = max(spokes, maskBox(rp - vec2(0.07, 0.0), vec2(0.045, 0.009)));
        }
        objMask = max(ring, spokes);
        objColor = mix(deep, bright, objMask * 0.75 + spokes * 0.25);
    }
    // Mode 5: Mono wireframe cube
    else if (mode > 4.5 && mode < 5.5) {
        vec2 q = rot2(t * 0.7) * lp;
        float front = maskBox(q, vec2(0.08, 0.08)) - maskBox(q, vec2(0.07, 0.07));
        vec2 off = vec2(0.04, 0.03);
        float back = maskBox(q + off, vec2(0.08, 0.08)) - maskBox(q + off, vec2(0.07, 0.07));
        float e1 = maskBox(q - vec2(-0.08, -0.08) + off * 0.5, vec2(0.004, 0.045));
        float e2 = maskBox(q - vec2(0.08, -0.08) + off * 0.5, vec2(0.004, 0.045));
        float e3 = maskBox(q - vec2(-0.08, 0.08) + off * 0.5, vec2(0.004, 0.045));
        float e4 = maskBox(q - vec2(0.08, 0.08) + off * 0.5, vec2(0.004, 0.045));
        objMask = max(max(front, back), max(max(e1, e2), max(e3, e4)));
        objColor = mix(deep, bright, objMask);
    }
    // Mode 6: Nostalgic compass + needle
    else if (mode > 5.5 && mode < 6.5) {
        float outer = maskCircle(lp, 0.13);
        float inner = maskCircle(lp, 0.11);
        float ring = max(0.0, outer - inner);
        vec2 np = rot2(t * 0.5) * lp;
        float needle = max(maskBox(np - vec2(0.05, 0.0), vec2(0.05, 0.01)), maskBox(np + vec2(0.05, 0.0), vec2(0.05, 0.01)));
        float hub = maskCircle(lp, 0.02);
        objMask = max(ring, max(needle, hub));
        objColor = mix(deep, bright, objMask);
    }
    // Mode 7: Power Surge Tesla coil
    else if (mode > 6.5 && mode < 7.5) {
        float tower = maskBox(lp - vec2(0.0, -0.08), vec2(0.02, 0.11));
        float cap = maskBox(lp + vec2(0.0, 0.04), vec2(0.05, 0.012));
        float arc1 = 1.0 - step(0.014, abs(lp.y - (0.08 + sin(lp.x * 30.0 + t * 8.0) * 0.02)));
        arc1 *= step(abs(lp.x), 0.22) * step(0.03, lp.y);
        float arc2 = 1.0 - step(0.014, abs(lp.y - (0.10 + sin(lp.x * 28.0 - t * 7.0) * 0.018)));
        arc2 *= step(abs(lp.x), 0.2) * step(0.03, lp.y);
        objMask = max(max(tower, cap), max(arc1, arc2));
        objColor = mix(vec3(0.7, 0.15, 0.2), vec3(1.0, 0.55, 0.58), objMask);
    }
    // Mode 8: Snow-white crystal snowflake
    else if (mode > 7.5 && mode < 8.5) {
        float snowfall = 0.0;
        // Larger pixel snowflakes falling from top to bottom.
        for (int i = 0; i < 16; i++) {
            float fi = float(i);
            float laneX = fract(hash(vec2(fi * 6.21, 1.37)) + fi * 0.173);
            float fallSpeed = 0.11 + 0.055 * fract(hash(vec2(fi * 2.71, 8.41)));
            float drift = sin(t * (1.2 + fi * 0.06) + fi * 1.9) * (0.015 + 0.016 * fract(hash(vec2(fi * 4.2, 5.7))));
            float yNorm = 1.14 - fract(t * fallSpeed + fi * 0.083) * 1.42;
            vec2 sf = vec2((uv.x - laneX) - drift, uv.y - yNorm);
            float flakeSize = 0.010 + 0.006 * fract(hash(vec2(fi * 10.13, 2.11)));
            float rot = t * (0.22 + 0.03 * fi);
            vec2 sfr = rot2(rot) * sf;

            float center = maskCircle(sfr, flakeSize * 0.48);
            float armH = maskBox(sfr, vec2(flakeSize * 1.7, flakeSize * 0.24));
            float armV = maskBox(sfr, vec2(flakeSize * 0.24, flakeSize * 1.7));
            float armD1 = maskBox(rot2(0.785) * sfr, vec2(flakeSize * 1.3, flakeSize * 0.2));
            float armD2 = maskBox(rot2(-0.785) * sfr, vec2(flakeSize * 1.3, flakeSize * 0.2));
            float oneFlake = max(center, max(max(armH, armV), max(armD1, armD2)));
            snowfall = max(snowfall, oneFlake);
        }

        objMask = snowfall;
        vec3 snowColor = mix(vec3(0.85, 0.93, 1.0), vec3(1.0), snowfall);
        objColor = snowColor;
    }
    // Mode 9: Ash-gray gear cluster
    else if (mode > 8.5 && mode < 9.5) {
        vec2 g1 = lp - vec2(-0.06, 0.0);
        vec2 g2 = lp - vec2(0.08, -0.02);
        float gear1 = maskCircle(g1, 0.08) - maskCircle(g1, 0.05);
        float gear2 = maskCircle(g2, 0.06) - maskCircle(g2, 0.035);
        float teeth = 0.0;
        for (int i = 0; i < 8; i++) {
            float a = t * 0.5 + float(i) * 0.785;
            teeth = max(teeth, maskBox(g1 - vec2(cos(a), sin(a)) * 0.09, vec2(0.008, 0.008)));
        }
        for (int i = 0; i < 8; i++) {
            float a = -t * 0.7 + float(i) * 0.785;
            teeth = max(teeth, maskBox(g2 - vec2(cos(a), sin(a)) * 0.07, vec2(0.007, 0.007)));
        }
        objMask = max(max(gear1, gear2), teeth);
        objColor = mix(vec3(0.45, 0.5, 0.55), vec3(0.82, 0.86, 0.9), objMask);
    }
    // Mode 10: Cupid heart-locket
    else if (mode > 9.5 && mode < 10.5) {
        float open = 0.35 + 0.2 * sin(t * 1.2);
        vec2 hp = lp;
        float left = maskCircle(hp - vec2(-0.035 - open * 0.02, 0.02), 0.05);
        float right = maskCircle(hp - vec2(0.035 + open * 0.02, 0.02), 0.05);
        float bottom = maskBox(rot2(0.785) * (hp + vec2(0.0, 0.035)), vec2(0.06, 0.06));
        float heart = min(1.0, (left + right) * bottom);
        float hinge = maskBox(hp + vec2(0.0, 0.005), vec2(0.008, 0.018));
        objMask = max(heart, hinge);
        objColor = mix(vec3(0.93, 0.43, 0.62), vec3(1.0, 0.75, 0.86), heart);
        objColor = mix(objColor, vec3(1.0, 0.9, 0.75), hinge);
    }
    // Mode 11: Sunset lighthouse + sweep beam
    else if (mode > 10.5 && mode < 11.5) {
        float tower = maskBox(lp - vec2(0.0, -0.05), vec2(0.03, 0.11));
        float top = maskBox(lp + vec2(0.0, 0.07), vec2(0.045, 0.018));
        float beamA = 1.0 - step(0.0, abs(lp.y - (0.07 + (lp.x - 0.03) * tan(sin(t * 0.5) * 0.45))) - 0.018);
        beamA *= step(0.03, lp.x) * (1.0 - step(0.34, lp.x));
        objMask = max(max(tower, top), beamA);
        objColor = mix(vec3(0.9, 0.5, 0.25), vec3(1.0, 0.82, 0.55), objMask);
    }
    // Mode 12: Forest firefly jar
    else if (mode > 11.5 && mode < 12.5) {
        float jar = maskBox(lp - vec2(0.0, -0.02), vec2(0.09, 0.11)) - maskBox(lp - vec2(0.0, -0.02), vec2(0.075, 0.095));
        float lid = maskBox(lp + vec2(0.0, 0.095), vec2(0.07, 0.018));
        float fireflies = 0.0;
        for (int i = 0; i < 5; i++) {
            float fi = float(i);
            vec2 fp = lp - vec2(sin(t * 0.8 + fi * 1.7) * 0.05, -0.02 + cos(t * 0.9 + fi * 1.3) * 0.05);
            fireflies = max(fireflies, maskCircle(fp, 0.01));
        }
        objMask = max(max(jar, lid), fireflies);
        objColor = mix(vec3(0.42, 0.68, 0.44), vec3(0.92, 1.0, 0.68), fireflies);
        objColor = mix(objColor, vec3(0.7, 0.85, 0.72), max(jar, lid));
    }

    float scanA = sin((uv.y * resolution.y * 0.22) - (time * 5.0)) * 0.032;
    float scanB = sin((uv.y * resolution.y * 0.06) + (time * 1.8)) * 0.022;
    float grain = (hash(gl_FragCoord.xy + vec2(time * 37.0, time * 19.0)) - 0.5) * 0.05;
    float flicker = 0.94 + 0.06 * sin(time * 6.3);
    vec3 crtPass = (mix(tintA, tintB, 0.62)) * (scanA + scanB + grain) * flicker;

    float alphaObj = clamp(objMask * 0.52, 0.0, 0.62);
    float alphaCrt = 0.14;
    float outAlpha = clamp(alphaObj + alphaCrt, 0.0, 0.72);
    vec3 outColor = objColor * alphaObj + crtPass * alphaCrt;
    if (outAlpha > 0.0001) outColor /= outAlpha;
    outColor = floor(outColor * 8.0) / 8.0;
    gl_FragColor = vec4(outColor, outAlpha);
}
`;

const RADIO_TOP_SHADER_CRT = `
float hash(vec2 p) {
    return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453123);
}

void main() {
    vec2 uv = gl_FragCoord.xy / resolution.xy;

    float scanA = sin((uv.y * resolution.y * 0.22) - (time * 5.0)) * 0.042;
    float scanB = sin((uv.y * resolution.y * 0.06) + (time * 1.8)) * 0.028;
    float grain = (hash(gl_FragCoord.xy + vec2(time * 37.0, time * 19.0)) - 0.5) * 0.06;
    float flicker = 0.92 + 0.08 * sin(time * 6.3);

    float stripe = step(0.5, fract(uv.y * resolution.y * 0.5));
    vec3 tint = mix(tintA, tintB, 0.62);
    vec3 lineTint = mix(tint * 0.8, tint * 1.12, stripe);

    vec3 pass = lineTint * (scanA + scanB + grain) * flicker;
    gl_FragColor = vec4(pass, 0.17);
}
`;

function hexToRgbVector(hex, fallback) {
    if (typeof hex !== 'string') return fallback;
    const raw = hex.startsWith('#') ? hex.slice(1) : hex;
    const normalized = raw.length === 3
        ? raw.split('').map((char) => char + char).join('')
        : raw;
    if (!/^[0-9a-fA-F]{6}$/.test(normalized)) return fallback;
    return [
        Number.parseInt(normalized.slice(0, 2), 16) / 255,
        Number.parseInt(normalized.slice(2, 4), 16) / 255,
        Number.parseInt(normalized.slice(4, 6), 16) / 255,
    ];
}

function mixRgb(a, b, t) {
    return [
        a[0] + (b[0] - a[0]) * t,
        a[1] + (b[1] - a[1]) * t,
        a[2] + (b[2] - a[2]) * t,
    ];
}

function liftRgb(color, amount) {
    return [
        Math.min(1, color[0] + amount),
        Math.min(1, color[1] + amount),
        Math.min(1, color[2] + amount),
    ];
}

function rgbToHueDegrees(rgb) {
    const r = Math.max(0, Math.min(1, Number(rgb?.[0]) || 0));
    const g = Math.max(0, Math.min(1, Number(rgb?.[1]) || 0));
    const b = Math.max(0, Math.min(1, Number(rgb?.[2]) || 0));
    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    const delta = max - min;
    if (delta === 0) return 0;
    let h = 0;
    if (max === r) h = ((g - b) / delta) % 6;
    else if (max === g) h = (b - r) / delta + 2;
    else h = (r - g) / delta + 4;
    const deg = h * 60;
    return deg < 0 ? deg + 360 : deg;
}

const THEME_OBJECT_MODE = {
    'snow-white': 8,
};

const PREMIUM_THEME_IDS = new Set([
    'flexible-auto',
    'midnight-navy',
    'power-surge',
    'cyberpunk',
    'sky-blue',
    'snow-white',
    'nostalgic-wood',
    'mono-clean',
    'cupid-pink',
]);

const PIXEL_SCENE_THEME_IDS = new Set([
    'midnight-navy',
    'power-surge',
    'grand-prix',
    'cyberpunk',
    'sky-blue',
    'snow-white',
]);

function getPaletteGroup(palette) {
    const isPremiumTheme = PREMIUM_THEME_IDS.has(palette?.id)
        || Boolean(palette?.isPremium || palette?.premium || palette?.requiresPremium);
    if (isPremiumTheme) return 2;
    if (PIXEL_SCENE_THEME_IDS.has(palette?.id)) return 1;
    return 0;
}

function getPaletteGroupLabel(group) {
    if (group === 0) return 'STANDARD THEMES';
    if (group === 1) return 'PIXEL SCENE THEMES';
    return 'PREMIUM THEMES';
}

function pickAlbumImageBySize(images, targetSize = 64) {
    if (!Array.isArray(images) || images.length === 0) return null;
    const normalized = images
        .map((image) => ({
            url: image?.url || '',
            width: Number(image?.width || 0),
            height: Number(image?.height || 0),
        }))
        .filter((image) => Boolean(image.url));
    if (normalized.length === 0) return null;
    const exact = normalized.find((image) => image.width === targetSize || image.height === targetSize);
    if (exact) return exact.url;
    return normalized
        .slice()
        .sort((left, right) => {
            const leftDim = left.width || left.height || targetSize;
            const rightDim = right.width || right.height || targetSize;
            return Math.abs(leftDim - targetSize) - Math.abs(rightDim - targetSize);
        })[0]?.url || null;
}

export default function VintagePlayer({
    paletteId,
    paletteOptions,
    onPaletteChange,
    billingStatus,
    onOpenPremium,
    hasUploadedHistory,
    activeHistoryMode,
    onOpenDataHub,
    onOpenLibrary,
    activeBatchId,
    activeBatchLabel,
    radioLaunchContext = 'spotify',
}) {
    const { years, selectedYear, topTracks, topTracksYear, selectYear } = useHistory();
    const {
        isReady,
        playerError,
        isPlaying,
        currentTrack,
        playbackPosition,
        playbackDuration,
        queueTracks,
        playPlaylist,
        togglePlayPause,
        nextTrack,
        previousTrack,
        setVolume: setPlayerVolume,
        seekToPosition,
        reconnectPlayer,
        stopPlayback,
        clearLocalLibrary,
        loadLocalRemoteTracks,
        isLocalMode,
    } = usePlayer();
    const [volume, setVolume] = useState(70);
    const [uiPosition, setUiPosition] = useState(0);
    const [isScrubbing, setIsScrubbing] = useState(false);
    const [isPaletteMenuOpen, setIsPaletteMenuOpen] = useState(false);
    const [showRadioDisc, setShowRadioDisc] = useState(true);
    const [showPremiumThemeModal, setShowPremiumThemeModal] = useState(false);
    const [showPlayerWarning, setShowPlayerWarning] = useState(false);
    const [playerWarningMessage, setPlayerWarningMessage] = useState('');
    const [isParticlesInit, setIsParticlesInit] = useState(false);
    const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);
    const [equalizerBars, setEqualizerBars] = useState([45, 55, 65, 75, 85, 75, 65, 55, 45, 50, 60, 70]);
    const [yearSelectionNonce, setYearSelectionNonce] = useState(0);
    const [localLibraries, setLocalLibraries] = useState([]);
    const [localCurrentFolderId, setLocalCurrentFolderId] = useState("");
    const deviceScreenRef = useRef(null);
    const shaderCanvasRef = useRef(null);
    const shaderOverlayCanvasRef = useRef(null);
    const paletteMenuRef = useRef(null);
    const lastAutoPlayKeyRef = useRef('');
    const lastPlayerWarningRef = useRef('');
    const shuffleUris = useCallback((uris) => {
        const shuffled = [...uris];
        for (let i = shuffled.length - 1; i > 0; i -= 1) {
            const j = Math.floor(Math.random() * (i + 1));
            [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
        }
        return shuffled;
    }, []);

    // Build queue and start playing when year changes
    useEffect(() => {
        if (!selectedYear) return;
        if (topTracksYear != null && Number(topTracksYear) !== Number(selectedYear)) return;
        if (!selectedYear || topTracks.length === 0) return;

        const trackUris = topTracks
            .map((track) => {
                const directUri = track?.spotify_uri || track?.uri;
                if (typeof directUri === 'string' && directUri.startsWith('spotify:track:')) {
                    return directUri;
                }
                const trackId = track?.spotify_track_id;
                if (typeof trackId === 'string' && trackId.trim()) {
                    return `spotify:track:${trackId.trim()}`;
                }
                return null;
            })
            .filter(Boolean);
        if (trackUris.length === 0) return;

        const batchKey = activeBatchId ? String(activeBatchId) : 'no-batch';
        const stableTrackKey = [...new Set(trackUris)].sort().join('|');
        const autoPlayKey = `${batchKey}:${selectedYear}:${stableTrackKey}:${yearSelectionNonce}`;
        if (lastAutoPlayKeyRef.current === autoPlayKey) return;

        lastAutoPlayKeyRef.current = autoPlayKey;
        const shuffledUris = shuffleUris(trackUris);
        playPlaylist(shuffledUris);
    }, [selectedYear, topTracksYear, topTracks, playPlaylist, shuffleUris, activeBatchId, yearSelectionNonce]);

    useEffect(() => {
        const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
        const applyMotionPreference = () => {
            setPrefersReducedMotion(mediaQuery.matches);
        };
        applyMotionPreference();
        if (typeof mediaQuery.addEventListener === 'function') {
            mediaQuery.addEventListener('change', applyMotionPreference);
            return () => mediaQuery.removeEventListener('change', applyMotionPreference);
        }
        mediaQuery.addListener(applyMotionPreference);
        return () => mediaQuery.removeListener(applyMotionPreference);
    }, []);

    // Update equalizer pattern when current track changes
    useEffect(() => {
        if (currentTrack) {
            const pattern = getTrackPattern(currentTrack);
            setEqualizerBars(pattern);
        }
    }, [currentTrack]);

    useEffect(() => {
        const message = String(playerError || '').trim();
        if (!message) return;
        const normalized = message.toLowerCase();
        const shouldPopup = normalized.includes('disconnected')
            || normalized.includes('unavailable')
            || normalized.includes('session expired')
            || normalized.includes('rate-limiting');
        if (!shouldPopup) return;
        if (lastPlayerWarningRef.current === message) return;
        lastPlayerWarningRef.current = message;
        setPlayerWarningMessage(message);
        setShowPlayerWarning(true);
    }, [playerError]);

    // Debounce volume updates to avoid flooding playback control requests
    useEffect(() => {
        const timer = window.setTimeout(() => {
            setPlayerVolume(volume);
        }, 120);
        return () => window.clearTimeout(timer);
    }, [volume, setPlayerVolume]);

    useEffect(() => {
        const handleDocumentClick = (event) => {
            if (paletteMenuRef.current && !paletteMenuRef.current.contains(event.target)) {
                setIsPaletteMenuOpen(false);
            }
        };
        document.addEventListener('mousedown', handleDocumentClick);
        return () => {
            document.removeEventListener('mousedown', handleDocumentClick);
        };
    }, []);

    // Handle spacebar for play/pause
    useEffect(() => {
        const handleKeyDown = (event) => {
            if (event.code === 'Space') {
                event.preventDefault();
                togglePlayPause();
            }
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [togglePlayPause]);

    const handlePlayPause = () => {
        togglePlayPause();
    };

    const handleNext = () => {
        nextTrack();
    };

    const handlePrevious = () => {
        previousTrack();
    };

    const handleLoadLibraryFolder = useCallback(
        async (library, startIndex = 0) => {
            if (!library || !Array.isArray(library.tracks)) return;
            await loadLocalRemoteTracks(library.tracks, startIndex);
        },
        [loadLocalRemoteTracks]
    );

    const handleYearSelect = (year) => {
        if (selectedYear !== year) {
            selectYear(year);
        }
        setYearSelectionNonce((value) => value + 1);
    };

    const albumArtUrl = useMemo(
        () => currentTrack?.album?.images?.[0]?.url || null,
        [currentTrack?.album?.images]
    );
    const albumArt64Url = useMemo(
        () => pickAlbumImageBySize(currentTrack?.album?.images, 64),
        [currentTrack?.album?.images]
    );
    const trackName = currentTrack?.name || 'Unknown Track';
    const artistName =
        currentTrack?.artists?.map((artist) => artist?.name).filter(Boolean).join(', ') || 'Unknown Artist';
    const validYears = (years || [])
        .filter((year) => Number.isInteger(year) && year >= 1900 && year <= 2100);
    const upNextTracks = queueTracks
        .filter((track) => track?.id && track.id !== currentTrack?.id)
        .slice(0, 5);
    const upNextSlots = Array.from({ length: 5 }, (_, idx) => upNextTracks[idx] || null);
    const selectedLocalLibrary = useMemo(
        () => localLibraries.find((library) => library.id === localCurrentFolderId) || null,
        [localLibraries, localCurrentFolderId]
    );
    const showYearItems = activeHistoryMode === 'yearly-hits' || hasUploadedHistory;
    const sidebarContentMode = radioLaunchContext === 'local' ? 'local' : 'spotify';
    const showSpotifySidebar = sidebarContentMode === 'spotify';
    const showLocalSidebar = sidebarContentMode === 'local';
    const sidebarHeaderLabel = showLocalSidebar && selectedLocalLibrary
        ? selectedLocalLibrary.title
        : showLocalSidebar
            ? 'LOCAL PLAYLISTS'
            : 'SPOTIFY PLAYLISTS';
    const localPlaylistBadge = useMemo(() => {
        if (!selectedLocalLibrary) return '';
        const trackCount = Array.isArray(selectedLocalLibrary.tracks)
            ? selectedLocalLibrary.tracks.length
            : 0;
        return `${selectedLocalLibrary.title} · ${trackCount} TRACK${trackCount === 1 ? '' : 'S'}`;
    }, [selectedLocalLibrary]);
    const selectedPaletteLabel = paletteOptions.find((palette) => palette.id === paletteId)?.label || 'Theme';
    const activePalette = useMemo(
        () => paletteOptions.find((palette) => palette.id === paletteId) || null,
        [paletteOptions, paletteId]
    );
    const localDiskThemeStyle = useMemo(() => {
        const vars = activePalette?.vars || {};
        const accent = hexToRgbVector(vars['--radio-accent'], [0.29, 0.64, 1.0]);
        const targetHue = rgbToHueDegrees(accent);
        // Source icon base is blue-ish, so rotate from a blue anchor hue.
        const baseHue = 215;
        const hueRotate = `${Math.round(targetHue - baseHue)}deg`;
        const satBoost = Math.max(0.9, Math.min(2.0, (1.05 + ((accent[0] + accent[1] + accent[2]) / 3) * 0.65)));
        const bright = Math.max(0.82, Math.min(1.2, (0.92 + ((accent[0] * 0.2126 + accent[1] * 0.7152 + accent[2] * 0.0722) * 0.24))));
        const style = {
            '--local-disk-hue-rotate': hueRotate,
            '--local-disk-saturate': String(Number(satBoost.toFixed(3))),
            '--local-disk-brightness': String(Number(bright.toFixed(3))),
            '--local-disk-contrast': '1.05',
            '--local-disk-grayscale': '0',
            '--local-disk-sepia': '0',
        };
        const themeId = String(paletteId || '').trim();
        const apply = (overrides) => Object.assign(style, overrides);

        if (themeId === 'charcoal-black' || themeId === 'ash-gray') {
            apply({
                '--local-disk-hue-rotate': '0deg',
                '--local-disk-saturate': '0.35',
                '--local-disk-brightness': '0.56',
                '--local-disk-contrast': '1.35',
                '--local-disk-grayscale': '0.9',
                '--local-disk-sepia': '0',
            });
        } else if (themeId === 'sage') {
            apply({
                '--local-disk-hue-rotate': '-108deg',
                '--local-disk-saturate': '0.45',
                '--local-disk-brightness': '0.86',
                '--local-disk-contrast': '1.08',
                '--local-disk-grayscale': '0.4',
                '--local-disk-sepia': '0.14',
            });
        } else if (themeId === 'midnight-navy') {
            apply({
                '--local-disk-hue-rotate': '10deg',
                '--local-disk-saturate': '0.88',
                '--local-disk-brightness': '0.58',
                '--local-disk-contrast': '1.28',
                '--local-disk-grayscale': '0.2',
                '--local-disk-sepia': '0',
            });
        } else if (themeId === 'forest-green') {
            apply({
                '--local-disk-hue-rotate': '-84deg',
                '--local-disk-saturate': '0.95',
                '--local-disk-brightness': '0.68',
                '--local-disk-contrast': '1.2',
                '--local-disk-grayscale': '0.15',
                '--local-disk-sepia': '0.22',
            });
        } else if (themeId === 'mono-clean') {
            apply({
                '--local-disk-hue-rotate': '0deg',
                '--local-disk-saturate': '0',
                '--local-disk-brightness': '1.55',
                '--local-disk-contrast': '2.4',
                '--local-disk-grayscale': '1',
                '--local-disk-sepia': '0',
            });
        } else if (themeId === 'cyberpunk') {
            apply({
                '--local-disk-hue-rotate': '72deg',
                '--local-disk-saturate': '1.45',
                '--local-disk-brightness': '0.94',
                '--local-disk-contrast': '1.2',
                '--local-disk-grayscale': '0',
                '--local-disk-sepia': '0.08',
            });
        } else if (themeId === 'nostalgic-wood') {
            apply({
                '--local-disk-hue-rotate': '-178deg',
                '--local-disk-saturate': '1.15',
                '--local-disk-brightness': '0.86',
                '--local-disk-contrast': '1.16',
                '--local-disk-grayscale': '0.08',
                '--local-disk-sepia': '0.5',
            });
        } else if (themeId === 'sky-blue') {
            apply({
                '--local-disk-hue-rotate': '0deg',
                '--local-disk-saturate': '1',
                '--local-disk-brightness': '1',
                '--local-disk-contrast': '1.03',
                '--local-disk-grayscale': '0',
                '--local-disk-sepia': '0',
            });
        } else if (themeId === 'snow-white') {
            apply({
                '--local-disk-hue-rotate': '0deg',
                '--local-disk-saturate': '0.3',
                '--local-disk-brightness': '1.42',
                '--local-disk-contrast': '1.38',
                '--local-disk-grayscale': '0.72',
                '--local-disk-sepia': '0',
            });
        }

        return style;
    }, [activePalette, paletteId]);
    const sortedPaletteOptions = useMemo(() => {
        const toRgb = (hex) => {
            if (typeof hex !== 'string' || !hex.startsWith('#')) return null;
            const raw = hex.slice(1);
            const normalized = raw.length === 3
                ? raw.split('').map((char) => char + char).join('')
                : raw;
            if (!/^[0-9a-fA-F]{6}$/.test(normalized)) return null;
            return {
                r: Number.parseInt(normalized.slice(0, 2), 16),
                g: Number.parseInt(normalized.slice(2, 4), 16),
                b: Number.parseInt(normalized.slice(4, 6), 16),
            };
        };
        const brightness = (palette) => {
            const candidates = [
                palette?.swatch,
                palette?.vars?.['--radio-primary'],
                palette?.vars?.['--radio-bg'],
            ];
            const firstRgb = candidates
                .map((candidate) => toRgb(candidate))
                .find(Boolean);
            const rgb = firstRgb || null;
            if (!rgb) return 0;
            return 0.2126 * rgb.r + 0.7152 * rgb.g + 0.0722 * rgb.b;
        };
        return [...paletteOptions]
            .map((palette) => ({
                ...palette,
                __group: getPaletteGroup(palette),
                __isPremiumTheme: PREMIUM_THEME_IDS.has(palette.id)
                    || Boolean(palette?.isPremium || palette?.premium || palette?.requiresPremium),
            }))
            .sort((a, b) => {
                if (a.__group !== b.__group) return a.__group - b.__group;
                if (a.__group === 2 || b.__group === 2) {
                    const premiumPriority = ['flexible-auto', 'mono-clean'];
                    const aPriority = premiumPriority.indexOf(a.id);
                    const bPriority = premiumPriority.indexOf(b.id);
                    if (aPriority !== -1 || bPriority !== -1) {
                        if (aPriority === -1) return 1;
                        if (bPriority === -1) return -1;
                        if (aPriority !== bPriority) return aPriority - bPriority;
                    }
                }
                const brightDiff = brightness(a) - brightness(b);
                if (Math.abs(brightDiff) > 0.0001) return brightDiff;
                return String(a.label || a.id).localeCompare(String(b.label || b.id));
            });
    }, [paletteOptions]);
    const showMoonShader = paletteId === 'midnight-navy' || paletteId === 'cyberpunk';
    const showSunShader = paletteId === 'sky-blue';
    const showCupidHearts = paletteId === 'cupid-pink';
    const showGrandPrixMotion = paletteId === 'grand-prix';
    const showCelestialShader = showMoonShader || showSunShader;
    const objectMode = THEME_OBJECT_MODE[paletteId] || 0;
    const isPremiumUser = Boolean(billingStatus?.is_premium);
    const canTogglePlayback = Boolean(
        isReady
        && (
            isLocalMode
            || selectedYear
            || currentTrack
            || (queueTracks || []).length > 0
        )
    );
    const isFlexibleTheme = paletteId === 'flexible-auto';
    const flexiblePaletteVersion = useFlexibleAlbumPalette({
        enabled: isFlexibleTheme,
        albumArtUrl,
        sampleImageUrl: albumArt64Url,
    });

    useEffect(() => {
        initParticlesEngine(async (engine) => {
            await loadSlim(engine);
            await loadSnowPreset(engine);
        }).then(() => {
            setIsParticlesInit(true);
        });
    }, []);

    useEffect(() => {
        if (showYearItems) return;
        if (selectedYear) return;
        if (!years || years.length === 0) return;
        selectYear(years[0]);
    }, [showYearItems, selectedYear, years, selectYear]);

    useEffect(() => {
        const syncPlaybackToLaunchContext = async () => {
            if (radioLaunchContext === 'local') {
                if (!isLocalMode) {
                    await stopPlayback({ resetPosition: true });
                }
                return;
            }
            if (isLocalMode) {
                await clearLocalLibrary();
            }
        };
        syncPlaybackToLaunchContext();
    }, [radioLaunchContext, isLocalMode, stopPlayback, clearLocalLibrary]);

    useEffect(() => {
        if (!showLocalSidebar) {
            setLocalCurrentFolderId("");
            return undefined;
        }
        let cancelled = false;

        const fetchLibraries = async () => {
            try {
                const response = await fetch(`${API_ORIGIN}/api/local-library/tracks`, {
                    credentials: 'include',
                });
                if (!response.ok) throw new Error('Failed to fetch local libraries');
                const payload = await response.json();
                if (cancelled) return;
                const libraries = Array.isArray(payload?.libraries) ? payload.libraries : [];
                setLocalLibraries(libraries);
                if (libraries.length === 0) {
                    setLocalCurrentFolderId("");
                    return;
                }
                if (!libraries.some((library) => library.id === localCurrentFolderId)) {
                    setLocalCurrentFolderId("");
                }
            } catch (_error) {
                if (!cancelled) setLocalLibraries([]);
            }
        };

        fetchLibraries();
        return () => {
            cancelled = true;
        };
    }, [showLocalSidebar, localCurrentFolderId]);

    const handleSidebarChevron = useCallback(() => {
        if (showLocalSidebar && selectedLocalLibrary) {
            setLocalCurrentFolderId("");
            return;
        }
        if (showLocalSidebar && onOpenLibrary) {
            onOpenLibrary();
            return;
        }
        if (onOpenDataHub) {
            onOpenDataHub();
        }
    }, [
        showLocalSidebar,
        selectedLocalLibrary,
        onOpenLibrary,
        onOpenDataHub,
    ]);

    useEffect(() => {
        const targetCanvas = shaderCanvasRef.current;
        if (!targetCanvas || !showCelestialShader) return undefined;

        const rootStyles = window.getComputedStyle(document.documentElement);
        const tintA = hexToRgbVector(rootStyles.getPropertyValue('--radio-bg'), [0.12, 0.19, 0.24]);
        const tintB = hexToRgbVector(rootStyles.getPropertyValue('--radio-primary'), [0.2, 0.38, 0.48]);

        const bgShaderCode = showMoonShader ? RADIO_BG_SHADER_MOON : RADIO_BG_SHADER_SUN;
        const shader = createRGBA(bgShaderCode, {
            target: targetCanvas,
            loop: !prefersReducedMotion,
            uniforms: {
                tintA: () => tintA,
                tintB: () => tintB,
            },
        });

        if (prefersReducedMotion) {
            shader.setUniform('tintA', tintA);
            shader.setUniform('tintB', tintB);
        }

        return () => {
            shader.destroy();
        };
    }, [activePalette, flexiblePaletteVersion, prefersReducedMotion, showCelestialShader, showMoonShader]);

    useEffect(() => {
        const overlayCanvas = shaderOverlayCanvasRef.current;
        if (!overlayCanvas) return undefined;

        const rootStyles = window.getComputedStyle(document.documentElement);
        const baseBg = hexToRgbVector(rootStyles.getPropertyValue('--radio-bg'), [0.12, 0.19, 0.24]);
        const baseAccent = hexToRgbVector(rootStyles.getPropertyValue('--radio-accent'), [0.56, 0.72, 0.86]);
        const baseText = hexToRgbVector(rootStyles.getPropertyValue('--radio-text'), [0.92, 0.95, 1.0]);
        const tintA = mixRgb(baseBg, baseAccent, 0.72);
        const tintB = liftRgb(mixRgb(baseAccent, baseText, 0.58), 0.06);
        const overlayShaderCode = showMoonShader
            ? RADIO_TOP_SHADER_MOON
            : (showSunShader
                ? RADIO_TOP_SHADER_SUN
                : (showCupidHearts
                    ? RADIO_TOP_SHADER_HEARTS
                    : (objectMode > 0 ? RADIO_TOP_SHADER_OBJECTS : RADIO_TOP_SHADER_CRT)));

        const overlayShader = createRGBA(overlayShaderCode, {
            target: overlayCanvas,
            loop: !prefersReducedMotion,
            uniforms: {
                tintA: () => tintA,
                tintB: () => tintB,
                mode: () => objectMode,
            },
        });

        if (prefersReducedMotion) {
            overlayShader.setUniform('tintA', tintA);
            overlayShader.setUniform('tintB', tintB);
            overlayShader.setUniform('mode', objectMode);
        }

        return () => {
            overlayShader.destroy();
        };
    }, [activePalette, flexiblePaletteVersion, prefersReducedMotion, showMoonShader, showSunShader, showCupidHearts, objectMode]);

    useEffect(() => {
        if (isScrubbing) return;
        setUiPosition(Number(playbackPosition || 0));
    }, [playbackPosition, isScrubbing]);

    useEffect(() => {
        if (isScrubbing || !isPlaying) return undefined;
        const timer = window.setInterval(() => {
            setUiPosition((prev) => {
                const max = Number(playbackDuration || 0);
                if (max <= 0) return prev;
                return Math.min(prev + 1000, max);
            });
        }, 1000);
        return () => window.clearInterval(timer);
    }, [isScrubbing, isPlaying, playbackDuration]);

    const formatMs = (value) => {
        const totalSec = Math.max(0, Math.floor((Number(value) || 0) / 1000));
        const minutes = Math.floor(totalSec / 60);
        const seconds = totalSec % 60;
        return `${minutes}:${String(seconds).padStart(2, '0')}`;
    };

    const handleSeekChange = (event) => {
        setUiPosition(Number(event.target.value || 0));
    };

    const commitSeek = async () => {
        const max = Number(playbackDuration || 0);
        const nextPosition = Math.max(0, Math.min(Number(uiPosition || 0), max));
        await seekToPosition(nextPosition);
        setIsScrubbing(false);
    };

    return (
        <div className={`vintage-player palette-${paletteId}`}>
            <div className="vintage-stage">
                {/* Device Bezel/Frame */}
                <div className="device-frame">
                    <div className="device-screen" ref={deviceScreenRef}>
                        {showCelestialShader && (
                            <div className="radio-shader-layer" aria-hidden="true">
                                <canvas ref={shaderCanvasRef} className="radio-shader-canvas" />
                            </div>
                        )}
                        <ThemeParticlesLayer
                            paletteId={paletteId}
                            isParticlesInit={isParticlesInit}
                            prefersReducedMotion={prefersReducedMotion}
                        />
                        {showGrandPrixMotion && (
                            <div className="f1-motion-layer" aria-hidden="true">
                                <div className="f1-track-perspective" />
                                <div className="f1-speed-lines f1-speed-lines--near" />
                                <div className="f1-speed-lines f1-speed-lines--far" />
                                <div className="f1-apex-glow" />
                                <div className="f1-timing-strip" />
                            </div>
                        )}
                        {/* Pixelation Effect */}
                        {/* <PixelationEffect targetRef={deviceScreenRef} granularity={1} fps={8} opacity={1} /> */}
                        <div className="screen-content">
                            {/* Sidebar - Year Playlists */}
                            <div className="sidebar">
                                <div className="sidebar-header" data-pixelation-ignore>
                                    {(selectedLocalLibrary || onOpenLibrary || onOpenDataHub) && (
                                        <Button
                                            type="button"
                                            className="history-chevron-btn sidebar-chevron-btn"
                                            onClick={handleSidebarChevron}
                                            title={
                                                showLocalSidebar && selectedLocalLibrary
                                                    ? "Back to playlists"
                                                    : showLocalSidebar
                                                        ? "Open library"
                                                        : "Open data hub"
                                            }
                                        >
                                            <svg
                                                className="history-chevron-icon"
                                                viewBox="0 0 8 8"
                                                aria-hidden="true"
                                                focusable="false"
                                            >
                                                <path d="M5.5 1.5L2.5 4l3 2.5" />
                                            </svg>
                                    </Button>
                                    )}
                                    <span className="sidebar-header-text">
                                        {sidebarHeaderLabel}
                                    </span>
                                </div>
                                <div className="playlist-list">
                                    {showSpotifySidebar ? (
                                        <>
                                            {validYears.map((year) => (
                                                <Button
                                                    key={`spotify-${year}`}
                                                    className={`playlist-btn ${selectedYear === year ? 'active' : ''}`}
                                                    onClick={() => handleYearSelect(year)}
                                                >
                                                    <span className="music-emoji">{getYearEmoji(year)}</span>
                                                    {year}
                                                </Button>
                                            ))}
                                            {validYears.length === 0 && (
                                                <div className="playlist-list-placeholder" data-pixelation-ignore>
                                                    NO PLAYLIST DATA
                                                </div>
                                            )}
                                        </>
                                    ) : (
                                        <>
                                            {!selectedLocalLibrary ? (
                                                localLibraries.length > 0 ? (
                                                    <>
                                                        {localLibraries.map((library) => (
                                                            <Button
                                                                key={library.id}
                                                                className="playlist-btn local-folder-btn"
                                                                onClick={() => setLocalCurrentFolderId(library.id)}
                                                            >
                                                                <span className="folder-pixel-icon" aria-hidden="true">
                                                                    <svg viewBox="0 0 16 16" focusable="false">
                                                                        <path d="M1 4h5l1 2h8v8H1z" />
                                                                    </svg>
                                                                </span>
                                                                <span className="sidebar-marquee-wrap">
                                                                    <span
                                                                        className={`sidebar-marquee ${
                                                                            String(library.title || '').length > 16
                                                                                ? 'sidebar-marquee--animate'
                                                                                : ''
                                                                        }`}
                                                                    >
                                                                        <span>{library.title}</span>
                                                                        <span className="sidebar-marquee-copy" aria-hidden="true">
                                                                            {library.title}
                                                                        </span>
                                                                    </span>
                                                                </span>
                                                            </Button>
                                                        ))}
                                                    </>
                                                ) : (
                                                    <div className="local-library-hint">
                                                        No local playlists found.
                                                    </div>
                                                )
                                            ) : (
                                                <>
                                                    {selectedLocalLibrary.tracks.map((track, index) => (
                                                        <Button
                                                            key={track.id}
                                                            className={`playlist-btn ${
                                                                currentTrack?.name === track.name ? 'active' : ''
                                                            }`}
                                                            onClick={() => handleLoadLibraryFolder(selectedLocalLibrary, index)}
                                                        >
                                                            <span className="music-emoji">🎵</span>
                                                            <span className="sidebar-marquee-wrap">
                                                                <span
                                                                    className={`sidebar-marquee ${
                                                                        String(track.name || '').length > 16
                                                                            ? 'sidebar-marquee--animate'
                                                                            : ''
                                                                    }`}
                                                                >
                                                                    <span>{track.name || 'Unknown Track'}</span>
                                                                    <span className="sidebar-marquee-copy" aria-hidden="true">
                                                                        {track.name || 'Unknown Track'}
                                                                    </span>
                                                                </span>
                                                            </span>
                                                        </Button>
                                                    ))}
                                                </>
                                            )}
                                        </>
                                    )}
                                </div>
                            </div>

                            {/* Main Content */}
                            <div className="main-content">
                                {/* Top - Display Panel */}
                                <div className="cassette-display">
                                    <div className="cassette-header" data-pixelation-ignore>
                                        <div className="cassette-title-stack" data-pixelation-ignore>
                                            <div className="cassette-label" data-pixelation-ignore>RETROFY</div>
                                            {activeHistoryMode === 'time-travel-ready' && hasUploadedHistory && activeBatchLabel && (
                                                <div className="active-batch-badge" data-pixelation-ignore>
                                                    {activeBatchLabel}
                                                </div>
                                            )}
                                            {localPlaylistBadge && (
                                                <div className="active-batch-badge" data-pixelation-ignore>
                                                    {localPlaylistBadge}
                                                </div>
                                            )}
                                        </div>
                                        <div className="theme-toolbar" data-pixelation-ignore>
                                            <span className="palette-label" data-pixelation-ignore>
                                                THEME
                                            </span>
                                            <div
                                                className={`palette-select-wrap ${isPaletteMenuOpen ? 'open' : ''}`}
                                                ref={paletteMenuRef}
                                                data-pixelation-ignore
                                            >
                                                <Button
                                                    type="button"
                                                    className={`palette-select ${isPaletteMenuOpen ? 'open' : ''}`}
                                                    onClick={() => setIsPaletteMenuOpen((open) => !open)}
                                                    aria-haspopup="listbox"
                                                    aria-expanded={isPaletteMenuOpen}
                                                    aria-label="Theme"
                                                    data-pixelation-ignore
                                                >
                                                    <span className="palette-select-text" data-pixelation-ignore>{selectedPaletteLabel}</span>
                                                </Button>
                                                {isPaletteMenuOpen && (
                                                    <div className="palette-options" role="listbox" aria-label="Themes" data-pixelation-ignore>
                                                        {sortedPaletteOptions.map((palette, index) => (
                                                            <React.Fragment key={palette.id}>
                                                                {(index === 0
                                                                    || sortedPaletteOptions[index - 1]?.__group !== palette.__group) && (
                                                                    <div className="palette-group-label" data-pixelation-ignore>
                                                                        {getPaletteGroupLabel(palette.__group)}
                                                                    </div>
                                                                )}
                                                                <Button
                                                                    type="button"
                                                                    role="option"
                                                                    aria-selected={palette.id === paletteId}
                                                                    className={`palette-option ${palette.id === paletteId ? 'active' : ''} ${
                                                                        palette.__isPremiumTheme && !isPremiumUser ? 'palette-option--locked' : ''
                                                                    }`}
                                                                    onClick={() => {
                                                                        if (palette.__isPremiumTheme && !isPremiumUser) {
                                                                            setShowPremiumThemeModal(true);
                                                                            return;
                                                                        }
                                                                        onPaletteChange(palette.id);
                                                                        setIsPaletteMenuOpen(false);
                                                                    }}
                                                                    data-pixelation-ignore
                                                                >
                                                                    <span
                                                                        className={`palette-option-swatch ${
                                                                            palette.id === 'flexible-auto' ? 'palette-option-swatch--prismatic' : ''
                                                                        } ${
                                                                            palette.id === 'mono-clean' ? 'palette-option-swatch--mono-clean' : ''
                                                                        }`}
                                                                        style={{ background: palette.swatch || palette.vars['--radio-primary'] || palette.vars['--radio-bg'] }}
                                                                        aria-hidden="true"
                                                                    >
                                                                        {palette.__isPremiumTheme && !isPremiumUser && (
                                                                            <img
                                                                                src={`${process.env.PUBLIC_URL}/assets/icon-lock-8bit.svg`}
                                                                                alt=""
                                                                                className="palette-option-lock"
                                                                                aria-hidden="true"
                                                                            />
                                                                        )}
                                                                    </span>
                                                                    {palette.label}
                                                                </Button>
                                                            </React.Fragment>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="tape-deck">
                                        {currentTrack ? (
                                            <>
                                                <div className="track-display">
                                                    {/* Album Art enriched from Web API currently-playing endpoint */}
                                                    {isLocalMode ? (
                                                        <div className="album-art-container album-art-container--local" data-pixelation-ignore>
                                                            <div className="local-player-cd" style={localDiskThemeStyle} data-pixelation-ignore>
                                                                <div className="local-player-cd-rotor" aria-hidden="true">
                                                                    <img
                                                                        src={`${process.env.PUBLIC_URL}/assets/spinning-disc.png`}
                                                                        alt="Spinning Disc"
                                                                        className="local-player-cd-image"
                                                                        data-pixelation-ignore
                                                                    />
                                                                </div>
                                                            </div>
                                                        </div>
                                                    ) : albumArtUrl ? (
                                                        <div className="album-art-container album-art-container--radio" data-pixelation-ignore>
                                                            {showRadioDisc && (
                                                                <div
                                                                    className="spotify-disc-peek"
                                                                    aria-hidden="true"
                                                                >
                                                                    <div
                                                                        className="spotify-disc"
                                                                        style={{
                                                                            backgroundImage: `url(${albumArtUrl})`,
                                                                            animationPlayState: isPlaying ? 'running' : 'paused',
                                                                        }}
                                                                    />
                                                                </div>
                                                            )}
                                                            <img
                                                                src={albumArtUrl}
                                                                alt="Album Art"
                                                                className={`album-art ${showRadioDisc ? 'album-art--wedged' : ''}`}
                                                                data-pixelation-ignore
                                                            />
                                                            <div className="album-view-toggle">
                                                                <button
                                                                    type="button"
                                                                    className={`album-view-dot ${showRadioDisc ? 'active' : ''}`}
                                                                    onClick={() => setShowRadioDisc(true)}
                                                                    aria-label="Show spinning disc"
                                                                />
                                                                <button
                                                                    type="button"
                                                                    className={`album-view-dot ${!showRadioDisc ? 'active' : ''}`}
                                                                    onClick={() => setShowRadioDisc(false)}
                                                                    aria-label="Show cover only"
                                                                />
                                                            </div>
                                                        </div>
                                                    ) : null}
                                                    {/* Track Info */}
                                                    <div className="track-details" data-pixelation-ignore>
                                                        <div className="track-marquee-wrap" data-pixelation-ignore>
                                                            <div className="track-marquee" data-pixelation-ignore>
                                                                <div className="track-name" data-pixelation-ignore>
                                                                    {trackName}
                                                                </div>
                                                                <div className="artist-name" data-pixelation-ignore>
                                                                    {artistName}
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <div className="play-indicator" data-pixelation-ignore>
                                                            {isPlaying ? (
                                                                <>
                                                                    <span className="play-dots" data-pixelation-ignore>...</span> PLAYING
                                                                </>
                                                            ) : (
                                                                '▶ STOPPED'
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="queue-preview" data-pixelation-ignore>
                                                    <div className="queue-preview-title">UP NEXT</div>
                                                    <div className="queue-preview-list">
                                                        {upNextSlots.map((track, idx) => (
                                                            <div
                                                                key={`${track?.id || 'q'}-${idx}`}
                                                                className={`queue-preview-item ${track ? '' : 'queue-preview-item--empty'}`}
                                                            >
                                                                {track
                                                                    ? `${track.name || 'Unknown Track'} - ${(track.artists || []).map((artist) => artist?.name).filter(Boolean).join(', ') || 'Unknown Artist'}`
                                                                    : '---'}
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            </>
                                        ) : (
                                            <div className="no-track">No track selected</div>
                                        )}
                                    </div>
                                </div>

                                <div className="bottom-controls-layout">
                                    <div className="controls-section">
                                        {/* Middle - Equalizer */}
                                        <div className="equalizer">
                                            {equalizerBars.map((height, idx) => (
                                                <div
                                                    key={idx}
                                                    className="eq-bar"
                                                    style={{
                                                        '--base-height': `${height}%`,
                                                        height: `${height}%`
                                                    }}
                                                ></div>
                                            ))}
                                        </div>

                                        <div className="transport-panel">
                                            {/* Playback Controls */}
                                            <div className="playback-controls">
                                                <Button
                                                    className="control-btn prev-btn"
                                                    onClick={handlePrevious}
                                                    disabled={!isReady}
                                                >
                                                    <img
                                                        className="control-icon"
                                                        src={`${process.env.PUBLIC_URL}/assets/before-btn.png`}
                                                        alt="Previous"
                                                    />
                                                </Button>

                                                <Button
                                                    className={`control-btn play-btn ${isPlaying ? 'playing' : ''}`}
                                                    onClick={handlePlayPause}
                                                    disabled={!canTogglePlayback}
                                                >
                                                    {isPlaying ? (
                                                        <img
                                                            className="control-icon"
                                                            src={`${process.env.PUBLIC_URL}/assets/playing-button.png`}
                                                            alt="Pause"
                                                        />
                                                    ) : (
                                                        <img
                                                            className="control-icon"
                                                            src={`${process.env.PUBLIC_URL}/assets/paused.png`}
                                                            alt="Play"
                                                        />
                                                    )}
                                                </Button>

                                                <Button
                                                    className="control-btn next-btn"
                                                    onClick={handleNext}
                                                    disabled={!isReady}
                                                >
                                                    <img
                                                        className="control-icon"
                                                        src={`${process.env.PUBLIC_URL}/assets/before-btn.png`}
                                                        alt="Next"
                                                    />
                                                </Button>
                                            </div>

                                            {playerError ? (
                                                <div className="player-alert" data-pixelation-ignore>
                                                    <span className="player-alert-text">{playerError}</span>
                                                    <Button
                                                        type="button"
                                                        className="player-alert-reconnect"
                                                        onClick={reconnectPlayer}
                                                    >
                                                        RECONNECT
                                                    </Button>
                                                </div>
                                            ) : null}

                                            <div className="seek-control">
                                                <span className="seek-time">{formatMs(uiPosition)}</span>
                                                <input
                                                    type="range"
                                                    min="0"
                                                    max={Math.max(Number(playbackDuration || 0), 0)}
                                                    step="1000"
                                                    value={Math.min(Math.max(uiPosition, 0), Math.max(Number(playbackDuration || 0), 0))}
                                                    onMouseDown={() => setIsScrubbing(true)}
                                                    onTouchStart={() => setIsScrubbing(true)}
                                                    onChange={handleSeekChange}
                                                    onMouseUp={commitSeek}
                                                    onTouchEnd={commitSeek}
                                                    onKeyUp={commitSeek}
                                                    className="seek-slider"
                                                    disabled={!isReady || !currentTrack || Number(playbackDuration || 0) <= 0}
                                                />
                                                <span className="seek-time">{formatMs(playbackDuration)}</span>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="volume-control volume-control--dock">
                                        <div className="volume-slider-vertical-wrap">
                                            <input
                                                type="range"
                                                min="0"
                                                max="100"
                                                value={volume}
                                                onChange={(e) => setVolume(parseInt(e.target.value))}
                                                className="volume-slider volume-slider-vertical"
                                            />
                                        </div>
                                        <div className="volume-readout" data-pixelation-ignore>
                                            <span>🔊</span>VOL:{volume}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div
                            className={`radio-shader-overlay-layer ${(showCelestialShader || showCupidHearts) ? 'radio-shader-overlay-layer--moon' : ''}`}
                            aria-hidden="true"
                        >
                            <canvas ref={shaderOverlayCanvasRef} className="radio-shader-overlay-canvas" />
                        </div>
                    </div>
                </div>
            </div>
            <ConfirmModal
                isOpen={showPlayerWarning}
                title="PLAYBACK WARNING"
                message={playerWarningMessage}
                confirmText={
                    String(playerWarningMessage).toLowerCase().includes('rate-limiting')
                        ? 'OK'
                        : 'RECONNECT'
                }
                cancelText="LATER"
                onConfirm={async () => {
                    if (
                        String(playerWarningMessage).toLowerCase().includes('rate-limiting')
                    ) {
                        setShowPlayerWarning(false);
                        return;
                    }
                    await reconnectPlayer();
                    setShowPlayerWarning(false);
                }}
                onCancel={() => setShowPlayerWarning(false)}
                showCancel={
                    !String(playerWarningMessage).toLowerCase().includes('rate-limiting')
                }
                variant="warning"
            />
            <ConfirmModal
                isOpen={showPremiumThemeModal}
                title="PREMIUM THEME LOCKED"
                message="This theme is available for Premium users only. Open Premium plans to unlock it."
                confirmText="VIEW PLANS"
                cancelText="NOT NOW"
                onConfirm={() => {
                    setShowPremiumThemeModal(false);
                    setIsPaletteMenuOpen(false);
                    onOpenPremium?.();
                }}
                onCancel={() => setShowPremiumThemeModal(false)}
                showCancel
                variant="warning"
            />
        </div>
    );
}
